import React, { useEffect, useState } from "react";
import { X, Check, AlertCircle, Sparkles } from "lucide-react";
import api from "@/lib/api";

/**
 * Done-For-You request modal. Opens from:
 *  - FixModal per-artefact ("Have WiredGuru do this for you")
 *  - Services page ("Request")
 *
 * Props:
 *   open: bool
 *   service: { id, name, price_gbp, turnaround, description, bullets? } | null
 *   context: { project_id?, report_id?, finding_id?, action_title?, audit_url?, aeo_score? }
 *   onClose: () => void
 */
export default function DfyModal({ open, service, context, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setError(null);
      setMessage("");
      // Pre-fill email from auth cached user (if any) — best-effort
      (async () => {
        try {
          const r = await api.get("/auth/me");
          if (r.data?.email) setEmail(r.data.email);
          if (r.data?.name) setName(r.data.name);
        } catch {
          /* not signed in */
        }
      })();
    }
  }, [open]);

  if (!open || !service) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.post("/service-requests", {
        service_id: service.id,
        project_id: context?.project_id,
        report_id: context?.report_id,
        finding_id: context?.finding_id,
        action_title: context?.action_title,
        audit_url: context?.audit_url,
        aeo_score: context?.aeo_score,
        contact_email: email,
        contact_name: name || null,
        message: message || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="dfy-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border border-zinc-200 rounded-[6px] max-w-xl w-full max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-zinc-200">
          <div>
            <div className="label-eyebrow">
              <Sparkles size={11} className="inline -mt-0.5 mr-1" />
              WiredGuru · Done For You
            </div>
            <h3 className="display mt-1 text-xl font-bold tracking-tight">
              {service.name}
            </h3>
            {context?.action_title && (
              <div className="text-xs text-zinc-500 mt-0.5 truncate">
                Context: {context.action_title}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="dfy-close"
            className="text-zinc-400 hover:text-zinc-900 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-auto">
          {!submitted ? (
            <>
              <div className="grid grid-cols-2 gap-3 text-center">
                <Stat label="Turnaround" value={service.turnaround} />
                <Stat
                  label="Fixed price"
                  value={`£${service.price_gbp.toLocaleString()}`}
                />
              </div>
              <p className="text-sm text-zinc-700">{service.description}</p>
              {service.bullets && (
                <ul className="space-y-1.5 text-sm">
                  {service.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check
                        size={14}
                        className="mt-[3px] text-emerald-600 shrink-0"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              <form
                onSubmit={submit}
                className="space-y-3 border-t border-zinc-200 pt-4"
              >
                <Field
                  label="Your name"
                  value={name}
                  onChange={setName}
                  testid="dfy-name"
                />
                <Field
                  label="Contact email"
                  value={email}
                  onChange={setEmail}
                  type="email"
                  required
                  testid="dfy-email"
                />
                <div>
                  <label className="label-eyebrow">Notes (optional)</label>
                  <textarea
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Anything we should know before we quote?"
                    data-testid="dfy-message"
                    className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-[4px] p-2">
                    <AlertCircle size={14} /> {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || !email}
                  data-testid="dfy-submit"
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-black text-white text-sm px-4 py-2.5 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
                >
                  {loading ? "Submitting…" : "Request quote"}
                </button>
                <div className="text-[11px] text-zinc-500 text-center">
                  No payment today. WiredGuru will reach out within 1 business day.
                </div>
              </form>
            </>
          ) : (
            <div
              className="flex flex-col items-center text-center py-8"
              data-testid="dfy-success"
            >
              <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <Check size={20} />
              </div>
              <h4 className="display mt-4 text-xl font-bold tracking-tight">
                Request received
              </h4>
              <p className="mt-2 text-sm text-zinc-700 max-w-md">
                WiredGuru will reach out within 1 business day at{" "}
                <span className="mono">{email}</span> with a quote and next steps.
              </p>
              <button
                onClick={onClose}
                data-testid="dfy-done"
                className="mt-6 border border-zinc-900 text-sm px-4 py-2 rounded-[4px] hover:bg-zinc-100"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="label-eyebrow">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-zinc-200 rounded-[4px] p-3">
      <div className="label-eyebrow">{label}</div>
      <div className="display text-lg font-black mt-1 tabular-nums">{value}</div>
    </div>
  );
}
